<template>
  <div class="teacher-app-page attendance-page">
    <Toolbar />

    <v-container v-if="selectedClassRoom" class="attendance-page__content" fluid>
      <header class="attendance-heading">
        <div>
          <p class="teacher-page-eyebrow">Daily attendance</p>
          <h1>Take attendance</h1>
          <p>Tap each student to record their status.</p>
        </div>
      </header>

      <section class="attendance-controls" aria-label="Attendance settings">
        <div class="attendance-class-control">
          <div class="attendance-control-icon"><v-icon color="#2f7d4a">mdi-google-classroom</v-icon></div>
          <div class="attendance-control-copy">
            <label for="attendance-class">Class</label>
            <v-select
              id="attendance-class"
              v-model="selectedClassRoom"
              :items="classRooms"
              item-text="ClassRoom.name"
              return-object
              :menu-props="{ offsetY: true, nudgeBottom: 6 }"
              hide-details
              outlined
              dense
              aria-label="Select class"
            />
          </div>
        </div>

        <div class="attendance-date-control">
          <button type="button" aria-label="Previous day" @click="minus">
            <v-icon>mdi-chevron-left</v-icon>
          </button>
          <div>
            <strong>{{ selectedDate | moment("dddd") }}</strong>
            <span>{{ selectedDate | moment("MMMM D, YYYY") }}</span>
          </div>
          <button
            type="button"
            aria-label="Next day"
            :disabled="isToday"
            @click="plus"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </button>
        </div>
      </section>

      <section class="attendance-progress-card" aria-label="Attendance progress">
        <div class="attendance-progress-card__top">
          <div>
            <strong>{{ completedCount }} of {{ Students.length }} completed</strong>
            <span v-if="remainingCount">{{ remainingCount }} {{ remainingCount === 1 ? "student" : "students" }} left</span>
            <span v-else-if="Students.length">Attendance is complete</span>
            <span v-else>No students assigned</span>
          </div>
          <div class="attendance-progress-card__percent">{{ completionPercent }}%</div>
        </div>
        <v-progress-linear :value="completionPercent" height="7" rounded color="primary" background-color="#e7eee9" />

        <div class="attendance-summary">
          <div><span class="attendance-dot attendance-dot--present"></span><strong>{{ statusCounts.present }}</strong><small>Present</small></div>
          <div><span class="attendance-dot attendance-dot--absent"></span><strong>{{ statusCounts.absent }}</strong><small>Absent</small></div>
          <div><span class="attendance-dot attendance-dot--late"></span><strong>{{ statusCounts.late }}</strong><small>Late</small></div>
          <div><span class="attendance-dot attendance-dot--leave"></span><strong>{{ statusCounts.leave }}</strong><small>Leave</small></div>
        </div>
      </section>

      <section class="attendance-roster-card">
        <div class="teacher-section-heading attendance-roster-heading">
          <div>
            <h2>Student roster</h2>
            <p>{{ Students.length }} {{ Students.length === 1 ? "student" : "students" }}</p>
          </div>
          <span v-if="remainingCount" class="attendance-pending-badge">{{ remainingCount }} not set</span>
          <span v-else-if="Students.length" class="attendance-complete-badge">
            <v-icon size="14" color="#24713f">mdi-check</v-icon> Complete
          </span>
        </div>

        <div v-if="isLoading" class="teacher-loading-state" role="status">
          <v-progress-circular :size="40" :width="4" color="primary" indeterminate />
          <strong>Loading students</strong>
        </div>

        <div v-else-if="Students.length" class="attendance-roster">
          <template v-for="item in sortedStudents">
            <button
              v-if="item.Student"
              :key="item.StudentId"
              type="button"
              class="attendance-student-row"
              :class="`attendance-student-row--${normalizedStatus(item.status)}`"
              @click="takeAtt(item)"
            >
              <span class="teacher-student-row__avatar">{{ initials(item.Student.fullName) }}</span>
              <span class="attendance-student-row__copy">
                <strong>{{ item.Student.fullName }}</strong>
                <small v-if="item.Student.Customer && item.Student.Customer.fatherName">
                  Parent: {{ item.Student.Customer.fatherName }}
                </small>
                <small v-else>Tap to set attendance</small>
              </span>
              <span class="attendance-status" :class="`attendance-status--${normalizedStatus(item.status)}`">
                <v-icon size="15">{{ statusIcon(item.status) }}</v-icon>
                {{ statusLabel(item.status) }}
              </span>
              <v-icon size="18" color="#9aa6a0">mdi-chevron-right</v-icon>
            </button>
          </template>
        </div>

        <div v-else class="teacher-empty-state">
          <span><v-icon size="28" color="#2f7d4a">mdi-account-group-outline</v-icon></span>
          <h3>No students in this class</h3>
          <p>Add students from the home page before taking attendance.</p>
          <v-btn color="primary" elevation="0" @click="goHome">Return home</v-btn>
        </div>
      </section>
    </v-container>

    <v-bottom-sheet v-model="dialog" inset max-width="520" persistent content-class="attendance-sheet-wrap">
      <v-card v-if="selectedStudent" class="attendance-sheet" elevation="0">
        <div class="attendance-sheet__handle" aria-hidden="true"></div>
        <div class="attendance-sheet__header">
          <div class="teacher-student-row__avatar">{{ initials(selectedStudent.Student.fullName) }}</div>
          <div>
            <p>Set attendance for</p>
            <h2>{{ selectedStudent.Student.fullName }}</h2>
          </div>
          <v-btn icon aria-label="Close" @click="closeAttBox"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="attendance-sheet__body">
          <div class="attendance-status-options" role="group" aria-label="Attendance status">
            <button
              v-for="item in selectionObj"
              :key="item.label"
              type="button"
              :class="`attendance-option attendance-option--${item.label}`"
              :disabled="saving"
              @click="submitSelection(item)"
            >
              <span><v-icon>{{ item.icon }}</v-icon></span>
              <strong>{{ item.title }}</strong>
              <small>{{ item.description }}</small>
            </button>
          </div>

          <label class="attendance-reason-label" for="attendance-reason">Note <span>(optional)</span></label>
          <v-textarea
            id="attendance-reason"
            v-model="selectedStudent.reason"
            outlined
            hide-details
            auto-grow
            rows="2"
            placeholder="Add a reason for absence, late arrival, or leave"
          />
          <div v-if="saving" class="attendance-saving"><v-progress-circular size="18" width="2" color="primary" indeterminate /> Saving attendance…</div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <BottomNavigation />
  </div>
</template>

<script>
import attService from "@/modules/Attendance/service.js";
import classRoomService from "@/modules/ClassRoom/service.js";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import BottomNavigation from "@/modules/Layout/components/BottomNavigation.vue";
import sortStudentsByName from "@/utils/sortStudentsByName.js";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "teacher-attendance",
  components: { Toolbar, BottomNavigation },
  data() {
    return {
      OrgStudents: [],
      Attendance: null,
      selectionObj: [
        { label: "present", title: "Present", description: "In class today", icon: "mdi-check-circle-outline" },
        { label: "absent", title: "Absent", description: "Not in class", icon: "mdi-close-circle-outline" },
        { label: "late", title: "Late", description: "Arrived late", icon: "mdi-clock-outline" },
        { label: "leave", title: "Leave", description: "Approved leave", icon: "mdi-calendar-remove-outline" },
      ],
      dialog: false,
      saving: false,
      isLoading: true,
      selectedDate: this.$moment(),
      createDate: this.$moment(),
      todayDate: this.$moment(),
      Students: [],
      selectedStudent: null,
    };
  },
  async created() {
    document.title = "taleem - Attendance";
    if (!this.selectedClassRoom) this.$router.push({ path: "/" });
    else await this.loadAttendanceDay();
  },
  watch: {
    dialog(val) {
      if (!val) this.selectedStudent = null;
    },
    async selectedClassRoom(val, oldVal) {
      if (val && oldVal && val.ClassRoomId !== oldVal.ClassRoomId) await this.loadAttendanceDay();
    },
  },
  computed: {
    ...mapGetters("teacherLayout", ["getClassRoom", "getTeacherStore"]),
    selectedClassRoom: {
      get() { return this.getClassRoom; },
      set(val) { this.setClassRoom(val); },
    },
    Teacher: {
      get() { return this.getTeacherStore; },
      set(val) { this.setTeacher(val); },
    },
    classRooms() {
      return this.Teacher && this.Teacher.ClassRoomTeachers ? this.Teacher.ClassRoomTeachers : [this.selectedClassRoom];
    },
    statusCounts() {
      return this.Students.reduce((counts, student) => {
        const status = this.normalizedStatus(student.status);
        counts[status] = (counts[status] || 0) + 1;
        return counts;
      }, { present: 0, absent: 0, late: 0, leave: 0, notset: 0 });
    },
    completedCount() {
      return this.Students.length - this.statusCounts.notset;
    },
    remainingCount() {
      return this.statusCounts.notset;
    },
    completionPercent() {
      return this.Students.length ? Math.round((this.completedCount / this.Students.length) * 100) : 0;
    },
    isToday() {
      return this.todayDate.format("L") === this.selectedDate.format("L");
    },
    sortedStudents() {
      return sortStudentsByName(this.Students);
    },
  },
  methods: {
    ...mapActions("teacherLayout", ["setClassRoom", "setTeacher"]),
    initials(name) {
      if (!name) return "S";
      return name.split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    },
    normalizedStatus(status) {
      return status && status !== "not set" ? status.toLowerCase() : "notset";
    },
    statusLabel(status) {
      return this.normalizedStatus(status) === "notset" ? "Not set" : status;
    },
    statusIcon(status) {
      const icons = {
        present: "mdi-check-circle-outline",
        absent: "mdi-close-circle-outline",
        late: "mdi-clock-outline",
        leave: "mdi-calendar-remove-outline",
        notset: "mdi-plus-circle-outline",
      };
      return icons[this.normalizedStatus(status)];
    },
    async loadAttendanceDay() {
      this.isLoading = true;
      await this.getStudent();
      await this.getAtt();
      this.isLoading = false;
    },
    goHome() {
      this.$router.push({ path: "/" });
    },
    async submitSelection(item) {
      if (this.saving) return;
      this.saving = true;
      try {
        if (this.Attendance) {
          const detail = {
            AttendanceId: this.Attendance.id,
            status: item.label,
            StudentId: this.selectedStudent.Student.id,
            reason: this.selectedStudent.reason || "",
          };
          const response = await attService.createDetail(detail);
          this.Attendance = response.data;
        } else {
          const attendanceCreate = {
            attendanceDate: this.createDate.toISOString(),
            ClassRoomId: this.selectedClassRoom.ClassRoomId,
            TeacherId: this.selectedClassRoom.TeacherId,
            ClassId: this.selectedClassRoom.ClassRoom.ClassId,
            AttendanceDetail: this.Students.map(row => ({
              status: row.StudentId === this.selectedStudent.Student.id ? item.label : "not set",
              reason: row.StudentId === this.selectedStudent.Student.id ? (this.selectedStudent.reason || "") : "",
              StudentId: row.StudentId,
            })),
          };
          const response = await attService.create(attendanceCreate);
          this.Attendance = response.data;
        }
        this.setAttendance();
        this.dialog = false;
      } finally {
        this.saving = false;
      }
    },
    closeAttBox() {
      this.dialog = false;
    },
    takeAtt(student) {
      this.selectedStudent = student;
      this.dialog = true;
    },
    getStudent() {
      return classRoomService
        .getAssignStudentList({
          includeAttendance: true,
          ClassRoomId: this.selectedClassRoom.ClassRoomId,
          start: this.$moment(this.selectedDate).utc().startOf("day").toISOString(),
          end: this.$moment(this.selectedDate).utc().endOf("day").toISOString(),
        })
        .then(response => {
          this.Students = response.data;
          this.Students.forEach(row => {
            row.status = "not set";
            row.reason = null;
          });
          this.OrgStudents = JSON.parse(JSON.stringify(this.Students));
          return response;
        });
    },
    getAtt() {
      this.Attendance = null;
      const where = {
        start: this.$moment(this.selectedDate).utc().startOf("day").toISOString(),
        end: this.$moment(this.selectedDate).utc().endOf("day").toISOString(),
        ClassRoomId: this.selectedClassRoom.ClassRoomId,
      };
      return attService.getAll(where).then(response => {
        this.Attendance = response.data[0];
        this.setAttendance();
        return response;
      });
    },
    setAttendance() {
      this.Students = JSON.parse(JSON.stringify(this.OrgStudents));
      if (this.Attendance && this.Attendance.AttendanceDetails && this.Attendance.AttendanceDetails.length) {
        this.Students.forEach(student => {
          const detail = this.Attendance.AttendanceDetails.find(att => student.StudentId === att.StudentId);
          if (detail) {
            student.status = detail.status;
            student.reason = detail.reason;
          }
        });
      }
    },
    async minus() {
      this.selectedDate = this.$moment(this.selectedDate).subtract(1, "day");
      this.createDate = this.$moment(this.createDate).subtract(1, "day");
      await this.loadAttendanceDay();
    },
    async plus() {
      if (this.isToday) return;
      this.selectedDate = this.$moment(this.selectedDate).add(1, "day");
      this.createDate = this.$moment(this.createDate).add(1, "day");
      await this.loadAttendanceDay();
    },
  },
};
</script>
